import { Auth } from '@angular/fire/auth';
import { createApi } from '@core/api/base-api';
import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { PaginationInformation } from '@shared/pagination-server-side/datasources/genericBE.datasource';
import type { Menu, MenuWithSite, SyncMenuDto, UpdateMenuDto, MenuDayForDateResponse } from '@core/interfaces/api';

export interface DiningRdMenuSummary {
  MenuId: number;
  MenuName: string;
  FacilityId: number;
  FacilityName: string;
  Season: string;
  DaysInMenu: number;
}

export const createMenusApi = (auth: Auth) => {
  const api = createApi(auth);

  return {
    getSiteMealPlans: (
      organizationId: number,
      queryParams: HttpParams,
      pagination: PaginationInformation,
      siteName?: string,
      onlyDefault?: boolean,
    ) => {
      const { active, direction } = pagination.sort || {};

      let params = queryParams;
      params = params.append('sortBy', active || 'updatedOn');
      params = params.append('sortDirection', (direction || 'desc').toLowerCase());

      if (organizationId) {
        params = params.append('organizationId', organizationId.toString());
      }

      if (siteName) {
        params = params.append('search', siteName);
      }

      if (onlyDefault) {
        params = params.append('onlyDefault', 'true');
      }

      return api.get<{
        data: MenuWithSite[];
        totalCount: number;
      }>(`/menu/list`, { params });
    },

    getMenu: (siteId: number, cycleStartDate?: string) => {
      let params = new HttpParams();
      if (cycleStartDate) {
        params = params.append('cycleStartDate', cycleStartDate);
      }
      return api.get<Menu>(`/menu/${siteId}`, { params });
    },

    deleteSiteMenus: (siteId: number) =>
      api.delete<void>(`/menu/site-menus/${siteId}`),

    updateMenu: (data: UpdateMenuDto) =>
      api.patch<Menu>('/menu/update', data),

    syncMenu: (data: SyncMenuDto & { diningRdMenuId?: number }) =>
      api.post<Menu>('/menu/sync', data),

    syncAllMenus: (data: { siteId: number; propertyId: number; cycleStartDate: string }) =>
      api.post<Menu[]>('/menu/sync-all', data),

    setDefaultMenu: (siteId: number, diningRdMenuId: number) =>
      api.patch<void>(`/menu/${siteId}/default`, { diningRdMenuId }),

    getAvailableMenus: (propertyId: number) =>
      api.get<DiningRdMenuSummary[]>(`/menu/available-menus/${propertyId}`),

    getMenuHistory: (siteId: number) =>
      api.get<Menu[]>(`/menu/${siteId}/history`),

    getMenuForDate: (siteId: number, date: string) => {
      const params = new HttpParams().set('date', date);
      return api.get<MenuDayForDateResponse>(`/menu/${siteId}/date`, { params });
    },
  };
};

// Angular compatibility layer (temporary)
@Injectable({ providedIn: 'root' })
export class MenusApi {
  private readonly api = createMenusApi(this.auth);

  constructor(private auth: Auth) {}

  getSiteMealPlans = this.api.getSiteMealPlans;
  getMenu = this.api.getMenu;
  deleteSiteMenus = this.api.deleteSiteMenus;
  updateMenu = this.api.updateMenu;
  syncMenu = this.api.syncMenu;
  getMenuHistory = this.api.getMenuHistory;
}
